import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { InfoButton, SegmentedControl, segmentedControlClasses } from '@kc-console/common-ui';
import { DateRangePicker } from '@kc-console/shared-ui';

import { DEFAULT_PERIOD_PRESET, getPresetDateRange, PERIOD_PRESETS } from '../model/period';
import { useEventsDateFilter } from './EventsControllerProvider';

import type { PeriodPreset } from '../model/period';

type DateRange = [Date | null, Date | null];

const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  all: '전체',
  '24h': '최근 24시간',
  '7d': '최근 7일',
  '30d': '최근 30일',
  custom: '사용자화',
};

const SEGMENT_OPTIONS = PERIOD_PRESETS.map(preset => ({
  value: preset,
  label: PERIOD_PRESET_LABELS[preset],
}));

const isCompleteDateRange = (range: DateRange): range is [Date, Date] => {
  const [startDate, endDate] = range;
  return Boolean(startDate && endDate);
};

/**
 * 이벤트 내역 상단 날짜 필터.
 * @description
 * - 기간 프리셋 세그먼트(전체/최근 24시간/7일/30일/사용자화)와 조회 기간 선택기를 제공한다.
 * - `사용자화`가 아닌 프리셋 선택 시 계산된 기간을 비활성 상태로 표시한다.
 * - `사용자화` 선택 시 지난 1년 범위 안에서 직접 기간을 선택할 수 있다(1년 이전·미래 날짜 선택 불가).
 * @example
 * <EventDateFilter />
 */
const EventDateFilter = () => {
  const { periodPreset, customRange, onPresetChange, onCustomRangeChange } = useEventsDateFilter();
  const [draftRange, setDraftRange] = useState<DateRange>(customRange);
  // DateRangePicker가 닫힐 때 미완성 선택값을 직전 확정값으로 되돌리기 위해 분리해 둔다.
  const draftRangeRef = useRef<DateRange>(customRange);
  const committedRangeRef = useRef<DateRange>(customRange);

  const { minDate, maxDate } = useMemo(() => {
    const [min, max] = getPresetDateRange('all') ?? [null, null];
    return { minDate: min, maxDate: max };
  }, []);

  const isCustom = periodPreset === 'custom';
  const presetRange = getPresetDateRange(periodPreset);
  const [startDate, endDate] = isCustom ? draftRange : (presetRange ?? [null, null]);

  const handleDateRangeOpen = useCallback(() => {
    committedRangeRef.current = customRange;
    draftRangeRef.current = customRange;
    setDraftRange(customRange);
  }, [customRange]);

  const handleDateRangeClose = useCallback(() => {
    if (isCompleteDateRange(draftRangeRef.current)) return;

    const committedRange = committedRangeRef.current;
    draftRangeRef.current = committedRange;
    setDraftRange(committedRange);
  }, []);

  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      draftRangeRef.current = range;
      setDraftRange(range);

      if (!isCompleteDateRange(range)) return;

      committedRangeRef.current = range;
      onCustomRangeChange(range);
    },
    [onCustomRangeChange],
  );

  useEffect(() => {
    committedRangeRef.current = customRange;
    draftRangeRef.current = customRange;
    setDraftRange(customRange);
  }, [customRange]);

  return (
    <Root>
      <SegmentedControlStyled
        options={SEGMENT_OPTIONS}
        value={periodPreset}
        defaultValue={DEFAULT_PERIOD_PRESET}
        onChange={(_, value) => onPresetChange(value)}
      />
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateRangeChange}
        onOpen={handleDateRangeOpen}
        onClose={handleDateRangeClose}
        disabled={!isCustom}
        minDate={minDate}
        maxDate={maxDate}
        shouldCloseOnSelect
      />
      <InfoButton
        size="md"
        arrow="down"
        tooltipProps={{ message: '지난 1년 동안의 이벤트 내역을 조회할 수 있습니다.' }}
      />
    </Root>
  );
};

export default EventDateFilter;

const Root = styled('div')(
  () => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
  `,
);

const SegmentedControlStyled = styled(SegmentedControl)`
  .${segmentedControlClasses.item} {
    white-space: nowrap;
  }
` as typeof SegmentedControl;
